import React from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import isNil from 'lodash/isNil'
import isString from 'lodash/isString'
import moment from 'moment'
import { TimePicker } from 'src/components'
import { mergeProps } from 'src/util'
import { useHandler } from 'src/hooks'
import styles from './DurationInput.scss'

/** converts milliseconds to a moment date */
const toMoment = ms =>
  isNil(ms) || (isString(ms) && !ms)
    ? null
    : moment()
        .startOf('day')
        .add(moment.duration(ms))

/** converts a moment date to a duration in milliseconds */
const toDuration = date => (isNil(date) ? null : moment.duration(date.diff(moment().startOf('day'))).as('milliseconds'))

// set default open value to 00:00:00
const defaultOpenValue = moment().startOf('day')

const DurationInput = ({ value, onChange, color, error, ...props }) => {
  // convert value to moment
  value = toMoment(value)
  // onChange override to convert timepicker moment-like to duration
  const enhancedOnChange = useHandler(value => onChange && onChange(toDuration(value)))

  return (
    <TimePicker
      {...mergeProps()({ className: [styles.picker, { [styles.error]: error }] }, props)}
      defaultOpenValue={defaultOpenValue}
      value={value}
      onChange={enhancedOnChange}
      focusOnOpen
      use12Hours={false}
      allowEmpty={false}
      autoComplete="off"
    />
  )
}

DurationInput.defaultProps = {
  onChange: noop,
  color: 'light',
}

DurationInput.propTypes = {
  /**
   * The input & popup color.
   */
  color: PropTypes.string,
  /**
   * Time picker error.
   */
  error: PropTypes.node,
  /**
   * The duration value (timestamp, date or moment instance).
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  /**
   * An on change callback.
   */
  onChange: PropTypes.func,
  /**
   * An on blur callback.
   */
  onBlur: PropTypes.func,
}

export default DurationInput
